import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account } from '@/api/accounts';
import { aiMatchApi } from '@/api/ai';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { plannedApi, type Occurrence, type Series } from '@/api/planned';
import { reimbursementsApi, type ReimbursementLink } from '@/api/reimbursements';
import { reviewApi, reviewItemIsAi, type ReviewItem } from '@/api/review';
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import { transfersApi } from '@/api/transfers';
import { AiPill } from '@/components/AiPill';
import { HeadedCard } from '@/components/HeadedCard';
import { ErrorState } from '@/components/ErrorState';
import { AllocateDialog } from '@/features/accounts/AllocateDialog';
import { OwnedEnvelopesProvider } from '@/features/accounts/OwnedEnvelopesProvider';
import { OwnedEnvelopesContext } from '@/features/accounts/useOwnedEnvelopes';
import { canEditAllocations, isOwnerAccount } from '@/features/accounts/accountAccess';
import { PayeePrimary } from '@/features/payees/PayeePrimary';
import { PayeeProposalList } from '@/features/payees/PayeeProposalList';
import { OccurrenceSheet } from '@/features/planned/OccurrenceSheet';
import { closeTitle, isOutgoing } from '@/features/planned/paycheckParts';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import type { NotifyOptions } from '@/shell/NotifyProvider';
import type { ReviewCount } from '@/components/reviewTab';
import { generateRequestId } from '@/utils/requestId';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { MissingAccountCard } from './MissingAccountCard';
import { PaybackPairs, type PaybackPair, type PaybackSide } from './PaybackPairs';
import { isPartSuggestion, partialPaycheckCopy } from './partialPaycheck';

interface ReviewQueueProps {
  accountId?: number;
  /** The queue's one notification channel (TMMAPP-0124 item 13). */
  onNotify?: (message: string, options?: NotifyOptions) => void;
  /** Bump to force a refetch (e.g. after budget start changes). */
  reloadToken?: number;
  /**
   * The rows it shows, on every change (TMMAPP-0159 §F 27): the page's Items to review count. The
   * length of what was served for this account, never a read of its own.
   */
  onCount?: (count: ReviewCount) => void;
}

/** Allocation work the review read already limits to the budget start. */
function isAllocationItem(item: ReviewItem): boolean {
  return (
    item.kind !== 'MISSING_ACCOUNT' &&
    item.kind !== 'TRANSFER_CANDIDATE' &&
    item.kind !== 'TRANSFER_SUGGESTION'
  );
}

/**
 * The Review card. Empty renders nothing (the empty-section rule; TMMAPP-0154 item 16): the
 * account page's Items to review says "All caught up" (TMMAPP-0159 §F 27).
 */
function ReviewWorkCard({
  visible,
  headingRef,
  children,
}: {
  visible: boolean;
  headingRef?: Ref<HTMLHeadingElement>;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  if (!visible) return null;
  return (
    <HeadedCard
      title={t('review.title')}
      sx={{ mb: 2 }}
      role="group"
      aria-label={t('review.title')}
      {...(headingRef !== undefined ? { headingRef } : {})}
    >
      <Stack spacing={2}>{children}</Stack>
    </HeadedCard>
  );
}

/** Account suggestions and paybacks. Empty renders nothing. */
function TidyHistoryCard({
  visible,
  headingRef,
  children,
}: {
  visible: boolean;
  headingRef?: Ref<HTMLHeadingElement>;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  if (!visible) return null;
  return (
    <HeadedCard
      title={t('review.tidyTitle')}
      subtitle={t('review.tidySub')}
      sx={{ mb: 2 }}
      role="group"
      aria-label={t('review.tidyTitle')}
      {...(headingRef !== undefined ? { headingRef } : {})}
    >
      <Stack spacing={2}>{children}</Stack>
    </HeadedCard>
  );
}

function itemKey(item: ReviewItem, index: number): string {
  return [
    item.kind,
    item.seriesId ?? '',
    item.originalOn ?? '',
    item.transactionId ?? '',
    item.proposalId ?? '',
    index,
  ].join(':');
}

/**
 * The drawer reads the owned envelopes. An account page provides them; elsewhere (the Planned page)
 * the drawer brings its own provider, as the session drawer does (TMMAPP-0121 item 4).
 */
function WithOwnedEnvelopes({ provided, children }: { provided: boolean; children: ReactNode }) {
  return provided ? children : <OwnedEnvelopesProvider>{children}</OwnedEnvelopesProvider>;
}

export function ReviewQueue({ accountId, onNotify, reloadToken = 0, onCount }: ReviewQueueProps) {
  const { t } = useTranslation();
  const ownedEnvelopes = useContext(OwnedEnvelopesContext);
  const locale = useLocale();
  const me = useMe();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState<unknown>(undefined);
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [copy, setCopy] = useState<Record<string, string>>({});
  // A late part-paid occurrence's served view, by row (TMMAPP-0173 item 3).
  const [partials, setPartials] = useState<Record<string, Occurrence>>({});
  // "That's all" on a late part-paid occurrence, confirmed first.
  const [closing, setClosing] = useState<{ item: ReviewItem; occ: Occurrence | null } | null>(null);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [picker, setPicker] = useState<{
    item: ReviewItem;
    lines: OwnerRegisterLine[];
  } | null>(null);
  const [reDate, setReDate] = useState<{ item: ReviewItem; date: string } | null>(null);
  const [reDateBusy, setReDateBusy] = useState(false);
  // The re-date dialog is dirty once its date differs from the occurrence's; a save in flight is
  // not dismissible.
  const reDateGuard = useDialogDirtyGuard(
    reDate !== null && reDate.date !== (reDate.item.originalOn ?? ''),
    () => setReDate(null),
    reDateBusy,
    0,
    reDate !== null,
  );
  const [sheet, setSheet] = useState<{
    occ: Occurrence;
    series: Series | null;
    account: Account | null;
    /** "Match another deposit" opens the sheet at its match step (TMMAPP-0173 item 3). */
    initialView?: 'match';
  } | null>(null);
  const [drawer, setDrawer] = useState<{
    account: Account;
    line: OwnerRegisterLine;
    openSection?: 'transfer' | 'reimbursement';
    preferExpenseTransactionId?: number | null;
  } | null>(null);
  const [paybacks, setPaybacks] = useState<PaybackPair[]>([]);
  // The payback whose Yes needs an envelope (TMMAPP-0140 item 3): its card asks which one.
  const [envelopeLinkId, setEnvelopeLinkId] = useState<number | null>(null);
  // A Yes on a purchase already paid back (TMMAPP-0162 item 7): its warning stays until Pick another.
  const [paidBack, setPaidBack] = useState<ReimbursementLink | null>(null);
  // "Pick another" couldn't read the credit (TMMAPP-0164 item 7): the warning stays and says so.
  const [paidBackOpenFailed, setPaidBackOpenFailed] = useState(false);
  const started = useRef(false);
  // Each load takes a ticket; only the latest one's answer lands (TMMAPP-0124 item 10).
  const loadTicket = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [focusCheck, setFocusCheck] = useState(0);

  const load = useCallback(async () => {
    const ticket = ++loadTicket.current;
    // Once the queue is ready, a reload runs in the background (TMMAPP-0122 item 10): the card stays
    // mounted, so a decision's own focus handling (the next row or pair) survives the refetch.
    setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    try {
      const [list, accts, reimbPage] = await Promise.all([
        reviewApi.list(),
        accountsApi.list(),
        reimbursementsApi
          .list('PROPOSED')
          .catch(() => ({ items: [] as ReimbursementLink[], nextCursor: null })),
      ]);
      const filtered =
        accountId === undefined ? list : list.filter((row) => row.accountId === accountId);
      const nextCopy: Record<string, string> = {};
      const nextPartials: Record<string, Occurrence> = {};
      await Promise.all(
        filtered.map(async (item, index) => {
          const key = itemKey(item, index);
          // A paycheck in parts (TMMAPP-0173 items 3 and 4): read off the served occurrence or line.
          if (item.kind === 'PARTIAL_OVERDUE' || isPartSuggestion(item)) {
            try {
              const read = await partialPaycheckCopy(item, accts, me.user.currency, t, locale);
              if (read !== null) {
                nextCopy[key] = read.copy;
                if (read.occurrence !== null) nextPartials[key] = read.occurrence;
              }
            } catch {
              /* the row still shows its kind and its actions */
            }
            return;
          }
          if (
            (item.kind === 'MISSED' || item.kind === 'PLAN_PARTIAL') &&
            item.seriesId !== null &&
            item.originalOn !== null
          ) {
            const tl = await plannedApi.timeline(item.seriesId, {
              from: item.originalOn,
              to: item.originalOn,
            });
            const occ = tl[0];
            if (item.kind === 'MISSED') {
              nextCopy[key] =
                occ !== undefined
                  ? `${formatCivilDate(occ.occursOn, locale)} · ${occ.payee ?? ''}`
                  : item.originalOn;
              return;
            }
            const account = accts.find((row) => row.id === item.accountId);
            const currency = account?.currency ?? account?.operatingCurrency ?? me.user.currency;
            nextCopy[key] =
              occ !== undefined ? formatMoneyMinor(occ.remainderMinor, currency, locale) : '';
            return;
          }
          if (item.kind === 'CONFIRM_MATCH') {
            nextCopy[key] = t('review.candidates', { count: item.candidateTransactionIds.length });
            return;
          }
          if (item.kind === 'REPLACE_SYNTHETIC' && item.transactionId !== null) {
            nextCopy[key] = String(item.transactionId);
            return;
          }
          if (item.kind === 'RULE_PARTIAL' && item.transactionId !== null) {
            try {
              const line = await transactionsApi.get(item.accountId, item.transactionId);
              nextCopy[key] = `${line.id} · ${line.payee}`;
            } catch {
              nextCopy[key] = String(item.transactionId);
            }
            return;
          }
          if (
            (item.kind === 'TRANSFER_CANDIDATE' || item.kind === 'TRANSFER_SUGGESTION') &&
            item.transactionId !== null
          ) {
            try {
              const line = await transactionsApi.get(item.accountId, item.transactionId);
              const account = accts.find((row) => row.id === item.accountId);
              const currency = account?.currency ?? account?.operatingCurrency ?? me.user.currency;
              let counterparty = '';
              if (item.kind === 'TRANSFER_CANDIDATE' && item.candidateTransactionIds.length >= 2) {
                const otherId = item.candidateTransactionIds.find(
                  (id) => id !== item.transactionId,
                );
                if (otherId !== undefined) {
                  try {
                    const other = await transactionsApi.get(item.accountId, otherId);
                    counterparty = ` ↔ ${other.payee}`;
                  } catch {
                    counterparty = ` ↔ #${otherId}`;
                  }
                }
              } else if (item.kind === 'TRANSFER_SUGGESTION' && item.envelopeId !== null) {
                const dest = accts.find((row) => row.id === item.envelopeId);
                counterparty = dest !== undefined ? ` → ${dest.name}` : ` → #${item.envelopeId}`;
              }
              nextCopy[key] =
                `${formatCivilDate(line.postedOn, locale)} · ${line.payee} · ${formatMoneyMinor(line.operatingAmountMinor, currency, locale)}${counterparty}`;
            } catch {
              nextCopy[key] = String(item.transactionId);
            }
            return;
          }
          nextCopy[key] = t(`review.kind.${item.kind}`);
        }),
      );
      // Each side's account is on the link (TMMAPP-0097 B7): one request per side, no probing. A
      // side is structured (TMMAPP-0121 item 5); one that fails to load is null.
      const side = async (sideAccountId: number | null, txId: number) => {
        const acct = sideAccountId === null ? undefined : accts.find((a) => a.id === sideAccountId);
        if (acct === undefined) return null;
        try {
          const tx = await transactionsApi.get(acct.id, txId);
          return {
            accountId: acct.id,
            accountName: acct.name,
            postedOn: tx.postedOn,
            amountMinor: tx.amountMinor,
            currency: tx.currency || acct.currency || acct.operatingCurrency || null,
            operatingAmountMinor: tx.operatingAmountMinor,
            payee: tx.payeeRef?.name ?? tx.payee,
            bankText: tx.payeeRef === null,
            envelopes: tx.allocations.flatMap((a) =>
              a.envelopeName === null ? [] : [a.envelopeName],
            ),
            isTransfer: tx.transferAccountId !== null,
          } satisfies PaybackSide;
        } catch {
          return null;
        }
      };
      const loadedPairs = await Promise.all(
        reimbPage.items.map(async (link): Promise<PaybackPair> => {
          const [credit, expense] = await Promise.all([
            side(link.creditAccountId, link.creditTransactionId),
            side(link.expenseAccountId, link.expenseTransactionId),
          ]);
          return { link, credit, expense };
        }),
      );
      if (ticket !== loadTicket.current) return;
      setAccounts(accts);
      setCopy(nextCopy);
      setPartials(nextPartials);
      setItems(filtered);
      // A pair with a transfer on either side is never listed: confirming it would be refused
      // (`TRANSFER_NOT_ALLOCATABLE`). tmm-api withdraws these itself; the guard stays (item 4).
      // On an account's page, only the pairs with a side on it (TMMAPP-0159 §F 29): that account's
      // Tidy up. Each pair still shows on every account it touches.
      setPaybacks(
        loadedPairs.filter(
          (pair) =>
            !pair.credit?.isTransfer &&
            !pair.expense?.isTransfer &&
            (accountId === undefined ||
              pair.link.creditAccountId === accountId ||
              pair.link.expenseAccountId === accountId),
        ),
      );
      setStatus('ready');
    } catch (error) {
      if (ticket !== loadTicket.current) return;
      setLoadError(error);
      setStatus('error');
    }
  }, [accountId, locale, me.user.currency, t]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  useEffect(() => {
    if (reloadToken === 0) return;
    void reload(false);
    // reload closes over load; intentional bump-only trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reloadToken is the only intentional dep
  }, [reloadToken]);

  /**
   * `restoreFocus`: the reload follows a decision in the queue. If what had focus left with it (the
   * last row of a list, the last pair), focus moves to the queue's heading (WCAG 2.4.3).
   */
  async function reload(restoreFocus = true) {
    started.current = false;
    await load();
    started.current = true;
    if (restoreFocus) setFocusCheck((n) => n + 1);
  }

  useEffect(() => {
    if (focusCheck === 0) return;
    const active = document.activeElement;
    if (active === null || active === document.body || !active.isConnected) {
      // An emptied queue renders nothing (TMMAPP-0105 §C 4; TMMAPP-0154 item 16): the page's own
      // `h1` (PageHeader's `#page-title`, tabIndex -1) takes focus instead.
      (headingRef.current ?? document.getElementById('page-title'))?.focus();
    }
  }, [focusCheck]);

  /**
   * True once the write and the reload after it have both finished. `handled` takes a refusal the
   * caller answers in place (returning true), so it isn't announced.
   */
  async function runWrite(
    fn: () => Promise<void>,
    handled?: (error: unknown) => boolean,
  ): Promise<boolean> {
    try {
      await fn();
      await reload();
      return true;
    } catch (error) {
      if (handled?.(error) === true) return false;
      onNotify?.(
        isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED
          ? t('subscribe.cardTitle')
          : errorCopy(t, error, 'planned.errorGeneric'),
      );
      return false;
    }
  }

  async function handleMatchByHand(item: ReviewItem, initialView?: 'match') {
    if (item.seriesId === null || item.originalOn === null) return;
    const [tl, series] = await Promise.all([
      plannedApi.timeline(item.seriesId, { from: item.originalOn, to: item.originalOn }),
      plannedApi.getSeries(item.seriesId),
    ]);
    const occ = tl[0];
    if (occ === undefined) return;
    const account = accounts.find((a) => a.id === series.accountId) ?? null;
    setSheet({ occ, series, account, ...(initialView !== undefined ? { initialView } : {}) });
  }

  /** The item's account currency: a part-paid occurrence's figures are in it. */
  function closingCurrency(item: ReviewItem): string | null {
    const account = accounts.find((a) => a.id === item.accountId);
    return account?.currency ?? account?.operatingCurrency ?? me.user.currency;
  }

  /** "That's all" on a late part-paid occurrence: `…/close`, then the queue re-reads. */
  async function handleCloseShort() {
    if (closing === null || closeBusy) return;
    const { seriesId, originalOn } = closing.item;
    if (seriesId === null || originalOn === null) return;
    setCloseBusy(true);
    setCloseError(null);
    try {
      await plannedApi.close(seriesId, originalOn);
      setClosing(null);
      await reload();
    } catch (error) {
      setCloseError(errorCopy(t, error, 'planned.errorGeneric'));
    } finally {
      setCloseBusy(false);
    }
  }

  async function handleOpenPicker(item: ReviewItem) {
    const lines = await Promise.all(
      item.candidateTransactionIds.map((id) => transactionsApi.get(item.accountId, id)),
    );
    setPicker({ item, lines });
  }

  async function handleMatch(item: ReviewItem, transactionId: number) {
    const seriesId = item.seriesId;
    const originalOn = item.originalOn;
    if (seriesId === null || originalOn === null) return;
    await runWrite(async () => {
      await plannedApi.match(seriesId, originalOn, transactionId, generateRequestId());
      setPicker(null);
    });
  }

  async function handleOpenTransaction(item: ReviewItem) {
    if (item.transactionId === null) return;
    const [account, line] = await Promise.all([
      accountsApi.get(item.accountId),
      transactionsApi.get(item.accountId, item.transactionId),
    ]);
    setDrawer({ account, line });
  }

  async function handleAccept(item: ReviewItem) {
    const proposalId = item.proposalId;
    if (proposalId === null) return;
    await runWrite(() => aiMatchApi.accept(item.accountId, proposalId, generateRequestId()));
  }

  async function handleReject(item: ReviewItem) {
    const proposalId = item.proposalId;
    if (proposalId === null) return;
    await runWrite(() => aiMatchApi.reject(item.accountId, proposalId, generateRequestId()));
  }

  function dismissId(item: ReviewItem): number | null {
    if (item.kind === 'TRANSFER_SUGGESTION') return item.proposalId;
    return item.allocationId ?? item.transactionId ?? item.proposalId;
  }

  async function handleTransferYes(item: ReviewItem) {
    if (item.kind === 'TRANSFER_CANDIDATE') {
      const ids = item.candidateTransactionIds;
      if (ids.length < 2) return;
      await runWrite(async () => {
        await transfersApi.link(ids[0]!, ids[1]!);
      });
      return;
    }
    if (
      item.kind === 'TRANSFER_SUGGESTION' &&
      item.transactionId !== null &&
      item.envelopeId !== null
    ) {
      await runWrite(async () => {
        await transfersApi.classify(item.accountId, item.transactionId!, item.envelopeId!);
      });
    }
  }

  async function handleDismiss(item: ReviewItem) {
    const id = dismissId(item);
    if (id === null) return;
    try {
      await reviewApi.dismiss(item.kind, id);
      await reload();
    } catch (error) {
      if (isApiError(error) && error.status === 404) {
        await reload();
        return;
      }
      onNotify?.(
        isApiError(error) && error.code === ErrorCodes.REVIEW_NOT_DISMISSABLE
          ? t('review.notDismissable')
          : t('planned.errorGeneric'),
      );
    }
  }

  /**
   * A pair whose payback changed under Yes or No (`409 CONCURRENT_UPDATE`, TMMAPI-0180) says so in
   * the payback's words, not the envelope's, and the queue refetches (TMMAPP-0144 item 1).
   */
  function paybackChanged(error: unknown): boolean {
    if (!isApiError(error) || error.code !== ErrorCodes.CONCURRENT_UPDATE) return false;
    onNotify?.(t('reimb.changed'));
    void reload();
    return true;
  }

  /**
   * Yes confirms with no body. A purchase with no envelope answers `REIMBURSEMENT_TARGET_REQUIRED`
   * (TMMAPI-0179): the card asks for the envelope and resends with it, and the same amount as the
   * first attempt, which sent none (TMMAPP-0140 item 3). A purchase already paid back by the
   * paybacks confirmed on it answers `409 REIMBURSEMENT_EXCEEDS_EXPENSE` (TMMAPP-0162 item 7): the
   * warning takes the pair's place and the queue re-reads, as after any payback write. Never retried,
   * and its served figures are for logs, never copy.
   */
  async function handleReimbYes(link: ReimbursementLink, envelopeId?: number) {
    const done = await runWrite(
      async () => {
        await reimbursementsApi.confirm(
          link.id,
          envelopeId !== undefined ? { envelopeId } : undefined,
        );
        onNotify?.(t('reimb.confirmed', { envelope: '' }));
      },
      (error) => {
        if (paybackChanged(error)) {
          // The envelope question belonged to the link as it was; it closes with the refetch.
          setEnvelopeLinkId((prev) => (prev === link.id ? null : prev));
          return true;
        }
        if (isApiError(error) && error.code === ErrorCodes.REIMBURSEMENT_EXCEEDS_EXPENSE) {
          setEnvelopeLinkId((prev) => (prev === link.id ? null : prev));
          setPaidBack(link);
          setPaidBackOpenFailed(false);
          void reload();
          return true;
        }
        if (!isApiError(error) || error.code !== ErrorCodes.REIMBURSEMENT_TARGET_REQUIRED) {
          return false;
        }
        setEnvelopeLinkId(link.id);
        return true;
      },
    );
    if (done) setEnvelopeLinkId((prev) => (prev === link.id ? null : prev));
    return done;
  }

  async function handleReimbNo(link: ReimbursementLink) {
    return runWrite(async () => {
      await reimbursementsApi.reject(link.id);
      onNotify?.(t('reimb.rejected'));
    }, paybackChanged);
  }

  /**
   * Opens the credit line's drawer at a section: "Pick another" at the reimbursement section, "Mark
   * as transfer" at the transfer section (TMMAPP-0121 item 4). A classify there reloads the queue
   * through `onChanged`, and the pair leaves the list. Answers whether the drawer opened.
   */
  async function handleReimbDifferent(
    link: ReimbursementLink,
    section: 'transfer' | 'reimbursement' = 'reimbursement',
  ): Promise<boolean> {
    // The credit's account is on the link (TMMAPP-0097 B7): one request, no probing.
    const acct = accounts.find((a) => a.id === link.creditAccountId);
    if (acct === undefined) return false;
    try {
      const line = await transactionsApi.get(acct.id, link.creditTransactionId);
      setDrawer({
        account: acct,
        line,
        openSection: section,
        preferExpenseTransactionId: null,
      });
      return true;
    } catch {
      /* the credit is unavailable */
      return false;
    }
  }

  /** "Wrong purchase? Pick another": the warning clears only once the drawer opens (item 7). */
  async function pickAnotherForPaidBack(link: ReimbursementLink) {
    const opened = await handleReimbDifferent(link);
    if (opened) {
      setPaidBack((prev) => (prev === link ? null : prev));
      setPaidBackOpenFailed(false);
    } else {
      setPaidBackOpenFailed(true);
    }
  }

  /**
   * Opens a line the claim skipped (TMMAPP-0119 item 25). `skipped[]` names the line, not its
   * account: the item's account first, then the sample line's, then the owner's other managed
   * accounts in turn, until one has it. tmm-api doesn't serve the skipped line's `accountId` yet.
   */
  async function handleOpenSkipped(item: ReviewItem, transactionId: number) {
    const fresh = await accountsApi.list().catch(() => accounts);
    const owned = fresh.filter((a) => a.access === 'OWNER' && a.management === 'MANAGED');
    const first = owned.filter(
      (a) => a.id === item.accountId || a.name === item.sampleLine?.accountName,
    );
    const ordered = [...first, ...owned.filter((a) => !first.includes(a))];
    for (const account of ordered) {
      try {
        const line = await transactionsApi.get(account.id, transactionId);
        setDrawer({ account, line });
        return;
      } catch {
        /* not this account's line */
      }
    }
    // No account has it (TMMAPP-0122 item 16): say so, rather than doing nothing.
    onNotify?.(t('review.missingAccount.lineNotFound'), { severity: 'error' });
  }

  const subscriber = me.tier === 'SUBSCRIBER';
  const allocationItems = items.filter(
    (item) => isAllocationItem(item) && item.kind !== 'AI_PAYEE_PROPOSAL',
  );
  const transferItems = items.filter(
    (item) => item.kind === 'TRANSFER_CANDIDATE' || item.kind === 'TRANSFER_SUGGESTION',
  );
  const missingAccounts = items.filter(
    (item) => item.kind === 'MISSING_ACCOUNT' && item.mask !== null && item.mask.length > 0,
  );
  const payeeProposalCount = items.filter((item) => item.kind === 'AI_PAYEE_PROPOSAL').length;
  const hasPayeeProposals = payeeProposalCount > 0;
  const count: ReviewCount =
    status === 'loading'
      ? 'loading'
      : status === 'error'
        ? 'unavailable'
        : allocationItems.length +
          payeeProposalCount +
          transferItems.length +
          missingAccounts.length +
          paybacks.length;
  useEffect(() => {
    onCount?.(count);
  }, [count, onCount]);

  // The card is out of the way until there is something to review (TMMAPP-0105 §C 4; TMMAPP-0154
  // item 16, the empty-section rule): no skeleton, no empty card.
  if (status === 'loading') return null;

  if (status === 'error') {
    return (
      <Box sx={{ mb: 2 }}>
        <ErrorState
          variant="section"
          error={loadError}
          what={t('errors.what.reviewQueue')}
          onRetry={() => void reload(false)}
        />
      </Box>
    );
  }

  const reviewVisible = allocationItems.length > 0 || hasPayeeProposals;
  const tidyVisible =
    missingAccounts.length > 0 ||
    paybacks.length > 0 ||
    transferItems.length > 0 ||
    paidBack !== null;
  // An open drawer outlives a re-read that empties the queue (TMMAPP-0162 item 7): its cards go, it stays.
  if (!reviewVisible && !tidyVisible && drawer === null) return null;

  return (
    <>
      <ReviewWorkCard visible={reviewVisible} {...(reviewVisible ? { headingRef } : {})}>
        {hasPayeeProposals && (
          <Stack spacing={1}>
            <Typography variant="subtitle1" component="h3">
              {t('review.kind.AI_PAYEE_PROPOSAL')}
            </Typography>
            <PayeeProposalList
              embedded
              onNotify={onNotify}
              onDecided={() => {
                void reload();
              }}
            />
          </Stack>
        )}
        {allocationItems.length > 0 && (
          <Stack spacing={1}>
            {allocationItems.map((item, index) => {
              const key = itemKey(item, index);
              return (
                <Stack
                  key={key}
                  direction={{ xs: 'column', md: 'row' }}
                  sx={{ gap: 1, alignItems: { md: 'center' } }}
                >
                  <Typography sx={{ flex: 1 }}>
                    {t(`review.kind.${item.kind}`)}
                    {copy[key] !== undefined && copy[key] !== t(`review.kind.${item.kind}`)
                      ? ` · ${copy[key]}`
                      : ''}
                  </Typography>
                  {reviewItemIsAi(item) && <AiPill confidence={item.confidence} />}
                  {(item.payeeRef !== null || item.payee !== null) && (
                    <PayeePrimary bankText={item.payee ?? ''} payeeRef={item.payeeRef} />
                  )}
                  {item.kind === 'MISSED' && (
                    <>
                      <Button size="small" onClick={() => void handleMatchByHand(item)}>
                        {t('planned.matchByHand')}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setReDate({ item, date: item.originalOn ?? '' })}
                      >
                        {t('planned.reDate')}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const seriesId = item.seriesId;
                          const originalOn = item.originalOn;
                          if (seriesId === null || originalOn === null) return;
                          void runWrite(() => plannedApi.cancelOccurrence(seriesId, originalOn));
                        }}
                      >
                        {t('planned.cancelOccurrence')}
                      </Button>
                    </>
                  )}
                  {item.kind === 'CONFIRM_MATCH' &&
                    (isPartSuggestion(item) ? (
                      // A part suggestion confirms through the existing match (TMMAPP-0173 item 4).
                      <Button
                        size="small"
                        onClick={() => {
                          if (item.transactionId !== null) {
                            void handleMatch(item, item.transactionId);
                          }
                        }}
                      >
                        {t('review.confirmPart')}
                      </Button>
                    ) : (
                      <Button size="small" onClick={() => void handleOpenPicker(item)}>
                        {t('review.match')}
                      </Button>
                    ))}
                  {item.kind === 'PARTIAL_OVERDUE' && (
                    // No Wait: tmm-api serves no dismissal for it (REVIEW_NOT_DISMISSABLE; its G2).
                    <>
                      <Button
                        size="small"
                        onClick={() => {
                          setCloseError(null);
                          setClosing({ item, occ: partials[key] ?? null });
                        }}
                      >
                        {t('planned.parts.thatsAll')}
                      </Button>
                      <Button size="small" onClick={() => void handleMatchByHand(item, 'match')}>
                        {partials[key] !== undefined && isOutgoing(partials[key].amountMinor)
                          ? t('planned.parts.matchAnotherPaid')
                          : t('planned.parts.matchAnother')}
                      </Button>
                    </>
                  )}
                  {item.kind === 'REPLACE_SYNTHETIC' && (
                    <>
                      <Button size="small" onClick={() => void handleOpenTransaction(item)}>
                        {t('review.open')}
                      </Button>
                      <Button size="small" onClick={() => void handleOpenPicker(item)}>
                        {t('review.match')}
                      </Button>
                    </>
                  )}
                  {(item.kind === 'PLAN_PARTIAL' || item.kind === 'RULE_PARTIAL') && (
                    <Button size="small" onClick={() => void handleOpenTransaction(item)}>
                      {t('review.open')}
                    </Button>
                  )}
                  {item.kind === 'AI_MATCH_PROPOSAL' && (
                    <>
                      <Button size="small" onClick={() => void handleAccept(item)}>
                        {t('review.accept')}
                      </Button>
                      <Button size="small" onClick={() => void handleReject(item)}>
                        {t('review.reject')}
                      </Button>
                    </>
                  )}
                  {(item.kind === 'AI_MATCH_APPLIED' || item.kind === 'AI_CATEGORY_APPLIED') && (
                    <>
                      <Button size="small" onClick={() => void handleOpenTransaction(item)}>
                        {t('review.open')}
                      </Button>
                      <Button size="small" onClick={() => void handleDismiss(item)}>
                        {t('review.dismiss')}
                      </Button>
                    </>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </ReviewWorkCard>
      <TidyHistoryCard visible={tidyVisible} {...(!reviewVisible ? { headingRef } : {})}>
        {missingAccounts.map((item) => (
          <MissingAccountCard
            key={`missing-${item.mask}`}
            item={item}
            accounts={accounts}
            operatingCurrency={me.user.currency}
            onChanged={reload}
            onOpenLine={(transactionId) => void handleOpenSkipped(item, transactionId)}
          />
        ))}
        {paidBack !== null && (
          <Alert
            severity="warning"
            data-paid-back=""
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void pickAnotherForPaidBack(paidBack)}
              >
                {t('reimb.pickAnother')}
              </Button>
            }
          >
            {t('reimb.alreadyPaidBack')}
            {paidBackOpenFailed && (
              <Box component="span" data-paid-back-open-failed="" sx={{ display: 'block' }}>
                {t('reimb.pickAnotherFailed')}
              </Box>
            )}
          </Alert>
        )}
        {paybacks.length > 0 && (
          <PaybackPairs
            pairs={paybacks}
            pageAccountId={accountId}
            operatingCurrency={me.user.currency}
            envelopeLinkId={envelopeLinkId}
            handlers={{
              onYes: (link) => handleReimbYes(link),
              onPickEnvelope: handleReimbYes,
              onCancelEnvelope: () => setEnvelopeLinkId(null),
              onNo: handleReimbNo,
              onMarkTransfer: (link) => void handleReimbDifferent(link, 'transfer'),
              onPickAnother: (link) => void handleReimbDifferent(link),
            }}
          />
        )}
        {transferItems.length > 0 && (
          <Stack spacing={1}>
            {transferItems.map((item, index) => (
              <Stack
                key={itemKey(item, index)}
                direction={{ xs: 'column', md: 'row' }}
                sx={{ gap: 1, alignItems: { md: 'center' } }}
              >
                <Typography sx={{ flex: 1 }}>{t(`review.kind.${item.kind}`)}</Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void handleTransferYes(item)}
                >
                  {t('review.transferYes')}
                </Button>
                <Button size="small" onClick={() => void handleDismiss(item)}>
                  {t('review.transferNo')}
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </TidyHistoryCard>

      {picker !== null && (
        <Dialog open onClose={() => setPicker(null)} aria-labelledby="review-picker-title">
          <DialogTitle id="review-picker-title">{t('review.match')}</DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {picker.lines.map((line) => (
                <Button key={line.id} onClick={() => void handleMatch(picker.item, line.id)}>
                  {formatCivilDate(line.postedOn, locale)} · {line.payee}
                </Button>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPicker(null)}>{t('common.cancel')}</Button>
          </DialogActions>
        </Dialog>
      )}

      {reDate !== null && (
        <GuardedDialog guard={reDateGuard}>
          <DialogTitle>{t('planned.reDate')}</DialogTitle>
          <DialogContent>
            <TextField
              type="date"
              label={t('planned.occursOn')}
              value={reDate.date}
              onChange={(event) => setReDate({ ...reDate, date: event.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={reDateGuard.requestClose} disabled={reDateBusy}>
              {t('common.cancel')}
            </Button>
            <SubmitButton
              loading={reDateBusy}
              onClick={() => {
                const seriesId = reDate.item.seriesId;
                const originalOn = reDate.item.originalOn;
                const date = reDate.date;
                if (seriesId === null || originalOn === null) return;
                setReDateBusy(true);
                void runWrite(async () => {
                  await plannedApi.modifyOccurrence(seriesId, originalOn, { occursOn: date });
                  setReDate(null);
                }).finally(() => setReDateBusy(false));
              }}
            >
              {t('common.save')}
            </SubmitButton>
          </DialogActions>
        </GuardedDialog>
      )}

      {closing !== null && (
        <ConfirmDialog
          title={
            closing.occ !== null && closing.occ.receivedMinor !== null
              ? closeTitle(t, closing.occ, closingCurrency(closing.item), locale)
              : t('planned.parts.closeTitleUnknown')
          }
          consequence={t('planned.parts.closeConsequence')}
          confirmLabel={t('planned.parts.thatsAll')}
          busy={closeBusy}
          error={closeError}
          onConfirm={() => void handleCloseShort()}
          onCancel={() => {
            if (!closeBusy) setClosing(null);
          }}
        />
      )}

      {sheet !== null && (
        <WithOwnedEnvelopes provided={ownedEnvelopes !== null}>
          <OccurrenceSheet
            {...(sheet.initialView !== undefined ? { initialView: sheet.initialView } : {})}
            occurrence={sheet.occ}
            series={sheet.series}
            account={sheet.account}
            owner={sheet.account !== null && isOwnerAccount(sheet.account)}
            currency={
              sheet.account !== null
                ? (sheet.account.currency ?? sheet.account.operatingCurrency ?? me.user.currency)
                : me.user.currency
            }
            onClose={() => setSheet(null)}
            onChanged={async () => {
              await reload();
              // The sheet shows the occurrence as tmm-api now serves it (a part added or removed).
              const { seriesId, originalOn } = sheet.occ;
              const tl = await plannedApi
                .timeline(seriesId, { from: originalOn, to: originalOn })
                .catch(() => []);
              const next = tl.find((row) => row.originalOn === originalOn);
              if (next !== undefined) {
                setSheet((prev) =>
                  prev !== null &&
                  prev.occ.seriesId === seriesId &&
                  prev.occ.originalOn === originalOn
                    ? { ...prev, occ: next }
                    : prev,
                );
              }
            }}
            onEditFollowing={() => setSheet(null)}
            onEditSeries={() => setSheet(null)}
            onOpenSeries={() => setSheet(null)}
            onNotify={(message) => onNotify?.(message)}
          />
        </WithOwnedEnvelopes>
      )}

      {drawer !== null && (
        <WithOwnedEnvelopes provided={ownedEnvelopes !== null}>
          <AllocateDialog
            account={drawer.account}
            line={drawer.line}
            editor={subscriber && canEditAllocations(drawer.account)}
            owner={isOwnerAccount(drawer.account)}
            onClose={() => setDrawer(null)}
            onChanged={async (line) => {
              setDrawer((prev) => (prev !== null ? { ...prev, line } : prev));
              await reload();
            }}
            onNotify={(message) => onNotify?.(message)}
            {...(drawer.openSection !== undefined ? { openSection: drawer.openSection } : {})}
            preferExpenseTransactionId={drawer.preferExpenseTransactionId ?? null}
          />
        </WithOwnedEnvelopes>
      )}
    </>
  );
}
